import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Building2, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const { t } = useTranslation();
  const [view, setView] = useState('login'); // 'login', 'register', 'forgot', 'reset'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [forgotPasswordResult, setForgotPasswordResult] = useState(null);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();

  // Check for reset token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      setResetToken(token);
      setView('reset');
    }
  }, []);

  useEffect(() => {
    fetchCompanyProfileForLogin();
  }, []);

  const fetchCompanyProfileForLogin = async () => {
    try {
      const response = await axios.get(`${API}/company/profile/public`);
      setCompanyProfile(response.data);
    } catch (error) {
      console.error('Error fetching company profile for login:', error);
      // Set default fallback
      setCompanyProfile({
        name: 'Project Management',
        header_logo_file: null,
        logo_file: null
      });
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const success = await login(username, password);
      if (success) {
        // Don't reset loading state - let the redirect happen
        // The page will reload so the loading state doesn't matter
        return;
      }
    } catch (error) {
      console.error('handleLogin: Error', error);
    }
    
    // Only reset loading if login failed
    setIsLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate email domain - only allow expertfix.eu
    const allowedDomain = 'expertfix.eu';
    const emailDomain = email.split('@').pop()?.toLowerCase();
    if (emailDomain !== allowedDomain) {
      toast.error(`Registration is restricted to @${allowedDomain} email addresses only`);
      return;
    }
    
    setIsLoading(true);
    
    try {
      console.log('Registration attempt:', { email, username, fullName });
      const response = await axios.post(`${API}/auth/register`, {
        email,
        username,
        full_name: fullName,
        password
      });
      console.log('Registration response:', response.data);
      
      // Show success message with approval notice
      setRegistrationSuccess(true);
      toast.success(t('auto.misc.accountCreatedPleaseWaitForAdmin'));
      // Clear form
      setEmail('');
      setUsername('');
      setFullName('');
      setPassword('');
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Registration error response:', error.response?.data);
      
      let errorMessage = 'Registration failed. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.status === 400) {
        errorMessage = 'User with this email or username already exists.';
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setForgotPasswordResult(null);
    
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setForgotPasswordResult(response.data);
      
      if (response.data.method === 'email') {
        toast.success(t('auto.misc.ifAnAccountExistsResetInstructions'));
      } else {
        toast.info(t('auto.misc.pleaseContactYourAdministratorForPassword'));
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error(t('auto.misc.anErrorOccurredPleaseTryAgain'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error(t('auto.misc.passwordsDoNotMatch'));
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error(t('auto.misc.passwordMustBeAtLeast8'));
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password-confirm`, {
        token: resetToken,
        new_password: newPassword
      });
      
      toast.success(t('auto.misc.passwordResetSuccessfullyYouCanNow'));
      // Clear URL token and go back to login
      window.history.replaceState({}, document.title, window.location.pathname);
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');
      setView('login');
    } catch (error) {
      console.error('Reset password error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to reset password. The link may have expired.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getCardDescription = () => {
    switch (view) {
      case 'register':
        return 'Create your account to get started';
      case 'forgot':
        return 'Enter your email to receive reset instructions';
      case 'reset':
        return 'Create a new password for your account';
      default:
        return 'Sign in to your account to continue';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="space-y-1 text-center pb-8">
          <div className="mx-auto flex items-center justify-center mb-4">
            {companyProfile?.header_logo_file ? (
              <img 
                src={`${BACKEND_URL}/api/company/header-logo/${companyProfile.header_logo_file}`}
                alt={t('auto.misc.headerLogo')}
                className="h-20 w-auto object-contain"
              />
            ) : companyProfile?.logo_file ? (
              <img 
                src={`${BACKEND_URL}/api/company/logo/${companyProfile.logo_file}`}
                alt={t('auto.misc.companyLogo')}
                className="h-20 w-auto object-contain"
              />
            ) : (
              <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center">
                <Building2 className="w-8 h-8 text-white" />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight">
            {companyProfile?.name ? `${companyProfile.name} Project Management` : 'Project Management'}
          </CardTitle>
          <CardDescription className="text-base text-muted-foreground">
            {getCardDescription()}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Login View */}
          {view === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder={t('auto.misc.username')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="h-11"
                  data-testid="login-username"
                />
              </div>
              <div className="space-y-2">
                <Input
                  type="password"
                  placeholder={t('auto.misc.password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11"
                  data-testid="login-password"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-11 text-base font-medium"
                disabled={isLoading}
                data-testid="login-submit"
              >
                {isLoading ? t('auto.misc.signingIn') : t('auto.misc.signIn')}
              </Button>
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => setView('forgot')}
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  {t('auto.misc.forgotYourPassword')}
                </button>
              </div>
              <div className="text-center pt-2">
                <p className="text-sm text-slate-600">
                  Don&apos;t have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setView('register')}
                    className="text-primary hover:underline font-medium"
                  >
                    {t('auto.misc.createAccount')}
                  </button>
                </p>
              </div>
            </form>
          )}

          {/* Forgot Password View */}
          {view === 'forgot' && !forgotPasswordResult && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder={t('auto.misc.enterYourEmailAddress')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                  data-testid="forgot-email"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-11 text-base font-medium"
                disabled={isLoading}
                data-testid="forgot-submit"
              >
                {isLoading ? t('auto.misc.sending2') : t('auto.misc.sendResetInstructions')}
              </Button>
              <div className="text-center pt-4">
                <button
                  type="button"
                  onClick={() => setView('login')}
                  className="text-sm text-slate-600 hover:text-primary flex items-center justify-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('auto.misc.backToSignIn')}
                </button>
              </div>
            </form>
          )}

          {/* Forgot Password Result View */}
          {view === 'forgot' && forgotPasswordResult && (
            <div className="space-y-4 text-center py-4">
              {forgotPasswordResult.method === 'email' ? (
                <>
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{t('auto.misc.checkYourEmail')}</h3>
                  <p className="text-gray-600">
                    If an account with that email exists, we&apos;ve sent password reset instructions.
                  </p>
                  <p className="text-sm text-gray-500">
                    Check your spam folder if you don&apos;t see the email.
                  </p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{t('auto.misc.contactAdministrator')}</h3>
                  <p className="text-gray-600">
                    {forgotPasswordResult.message}
                  </p>
                  {forgotPasswordResult.admin_email && (
                    <p className="text-sm text-gray-700 bg-gray-100 p-3 rounded-md">
                      Admin email: <strong>{forgotPasswordResult.admin_email}</strong>
                    </p>
                  )}
                </>
              )}
              <Button 
                onClick={() => {
                  setForgotPasswordResult(null);
                  setEmail('');
                  setView('login');
                }}
                className="w-full h-11 text-base font-medium mt-4"
               data-testid="btn-back-to-sign-in">
                {t('auto.misc.backToSignIn')}
              </Button>
            </div>
          )}

          {/* Reset Password View (from email link) */}
          {view === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder={t('auto.misc.newPassword')}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="h-11 pr-10"
                    data-testid="reset-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('auto.misc.minimum8Characters')}
                </p>
              </div>
              <div className="space-y-2">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={t('auto.misc.confirmNewPassword')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-11"
                  data-testid="reset-confirm-password"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-11 text-base font-medium"
                disabled={isLoading || newPassword.length < 8 || newPassword !== confirmPassword}
                data-testid="reset-submit"
              >
                {isLoading ? t('auto.misc.resetting') : t('auto.misc.resetPassword')}
              </Button>
              <div className="text-center pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setResetToken('');
                    setNewPassword('');
                    setConfirmPassword('');
                    window.history.replaceState({}, document.title, window.location.pathname);
                    setView('login');
                  }}
                  className="text-sm text-slate-600 hover:text-primary flex items-center justify-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('auto.misc.backToSignIn')}
                </button>
              </div>
            </form>
          )}

          {/* Registration Success View */}
          {view === 'register' && registrationSuccess && (
            <div className="space-y-4 text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{t('auto.misc.accountCreated')}</h3>
              <p className="text-gray-600">
                {t('auto.misc.yourAccountHasBeenCreatedSuccessfully')}
              </p>
              <p className="text-sm text-gray-500">
                {t('auto.misc.youWillBeAbleToSign')}
              </p>
              <Button 
                onClick={() => {
                  setRegistrationSuccess(false);
                  setView('login');
                }}
                className="w-full h-11 text-base font-medium mt-4"
               data-testid="btn-back-to-sign-in">
                {t('auto.misc.backToSignIn')}
              </Button>
            </div>
          )}

          {/* Registration Form View */}
          {view === 'register' && !registrationSuccess && (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="Email Address (must be @expertfix.eu)"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                  data-testid="register-email"
                />
                <p className="text-xs text-muted-foreground">Only @expertfix.eu email addresses are allowed</p>
              </div>
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder={t('auto.misc.fullName')}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="h-11"
                  data-testid="register-fullname"
                />
              </div>
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder={t('auto.misc.username')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="h-11"
                  data-testid="register-username"
                />
              </div>
              <div className="space-y-2">
                <Input
                  type="password"
                  placeholder={t('auto.misc.password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11"
                  data-testid="register-password"
                />
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {t('auto.misc.newAccountsRequireAdministratorApprovalBefore')}
              </p>
              <Button 
                type="submit" 
                className="w-full h-11 text-base font-medium"
                disabled={isLoading}
                data-testid="register-submit"
              >
                {isLoading ? t('auto.misc.creatingAccount') : t('auto.misc.createAccount')}
              </Button>
              <div className="text-center pt-4">
                <p className="text-sm text-slate-600">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setView('login')}
                    className="text-primary hover:underline font-medium"
                  >
                    {t('auto.misc.signIn')}
                  </button>
                </p>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
